import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { copy } from "../copy";
import { formatBps, formatMoney } from "../format";
import { contestHref, pickKey, producerHref, type LandingLiveContest, type Pick } from "../feed";
import {
  bestPick,
  genresOf,
  liveDefaults,
  openInGenre,
  producerPlan,
  singlePicks,
  stackPicks,
  stakedCents,
  type FanLeg,
} from "../calculator";
import { saveGoal, type GoalInput } from "../goals";
import { useSession } from "../session";
import Icon from "./Icon";

type Role = GoalInput["role"];
type Focus = NonNullable<GoalInput["focus"]>;

const GOAL_PRESETS = [100, 300, 1000];

// Go serves this path with the calculator's own meta tags; it is not in
// SPA_PATHS, so links to it are full page loads.
export const CALCULATOR_PATH = "/earnings-calculator";
const t = copy.calculator;

// Whole dollars only: the goal and the assumptions are plans, not stakes.
function dollarsToCents(text: string): number | null {
  const trimmed = text.trim().replace(/,/g, "");
  if (!/^\d{1,7}$/.test(trimmed)) return null;
  const cents = Number(trimmed) * 100;
  return cents > 0 ? cents : null;
}

function Toggle<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">{label}</p>
      <div role="group" aria-label={label} className="mt-2 grid grid-cols-2 gap-1 rounded-lg border border-ground-edge bg-ground p-1">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={value === o.value}
            onClick={() => onChange(o.value)}
            className={`bf-focusable h-10 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
              value === o.value ? "bg-money text-ground" : "text-chrome-dim hover:text-chrome"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function DollarInput({
  id,
  label,
  value,
  onChange,
  big,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  big?: boolean;
}) {
  return (
    <div
      className={`flex items-center rounded-lg border border-ground-edge bg-ground focus-within:border-gold/60 ${big ? "h-14 px-4" : "h-10 px-3"}`}
    >
      <span className={`bf-tabular font-headline text-chrome-dim ${big ? "text-2xl" : "text-base"}`}>$</span>
      <input
        id={id}
        aria-label={label}
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`bf-tabular w-full bg-transparent pl-1 font-headline text-chrome outline-none ${big ? "text-3xl" : "text-base"}`}
      />
    </div>
  );
}

function Stat({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="rounded-xl border border-ground-edge bg-ground p-4">
      <p data-testid={testId} className="bf-tabular font-headline text-3xl leading-none text-gold md:text-4xl">
        {value}
      </p>
      <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-chrome-dim">{label}</p>
    </div>
  );
}

function Steps({ steps, links }: { steps: { title: string; body: string }[]; links?: (ReactNode | null)[] }) {
  return (
    <div className="mt-8">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">{t.stepsHeading}</p>
      <ol data-testid="calc-steps" className="mt-4 space-y-5">
        {steps.map((step, i) => (
          <li key={step.title} data-testid="calc-step" className="flex gap-4">
            <span className="bf-tabular font-headline text-2xl leading-none text-gold">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <p className="font-bold text-chrome">{step.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-chrome-dim">{step.body}</p>
              {links?.[i]}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

const stepLink = "bf-focusable mt-2 inline-block text-[11px] font-bold uppercase tracking-[0.14em] text-gold hover:text-gold-light";

// The contest's cover, or the disc the live contest cards fall back to.
function Thumb({ contest }: { contest: LandingLiveContest }) {
  return (
    <a
      href={contestHref(contest.slug)}
      tabIndex={-1}
      aria-hidden
      className="relative block h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-ground-edge bg-ground-raised"
    >
      {contest.cover_url ? (
        <img data-testid="calc-thumb" src={contest.cover_url} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_30%_20%,rgba(247,201,72,0.18),transparent_60%)]">
          <Icon name="disc" className="h-6 w-6 text-chrome-dim/60" />
        </span>
      )}
    </a>
  );
}

function SuggestedBattles({ contests }: { contests: LandingLiveContest[] }) {
  // Empty room rule: no open battles, no list.
  if (contests.length === 0) return null;
  const top = [...contests].sort((a, b) => b.pool_cents - a.pool_cents).slice(0, 3);
  return (
    <div className="mt-8">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">{t.suggestedHeading}</p>
      <ul data-testid="calc-battles" className="mt-3 divide-y divide-ground-edge rounded-xl border border-ground-edge bg-ground">
        {top.map((c) => (
          <li key={c.slug} data-testid="calc-battle" className="flex items-center gap-3 px-4 py-3">
            <Thumb contest={c} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-chrome">{c.name}</p>
              <p className="mt-0.5 text-[11px] uppercase tracking-[0.12em] text-chrome-dim">
                {c.pool_cents > 0 ? `${t.suggestedPot} ${formatMoney(c.pool_cents)}` : copy.live.emptyPool}
                {c.genre ? ` · ${c.genre}` : ""}
                {c.is_seeded && <span className="ml-2 text-gold">{t.seeded}</span>}
              </p>
            </div>
            <a
              href={contestHref(c.slug)}
              className="bf-focusable shrink-0 rounded-md border border-money/50 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-money hover:bg-money/10"
            >
              {t.enter}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProducerResult({
  goalCents,
  genre,
  focus,
  open,
}: {
  goalCents: number | null;
  genre: string;
  focus: Focus;
  open: LandingLiveContest[];
}) {
  const session = useSession();
  const defaults = useMemo(() => liveDefaults(open), [open]);
  const [potText, setPotText] = useState<string | null>(null);
  const [backText, setBackText] = useState<string | null>(null);
  // Until the visitor edits an assumption it follows the live battles.
  const potCents = potText === null ? defaults.potCents : dollarsToCents(potText);
  const backCents = backText === null ? defaults.backCents : dollarsToCents(backText);
  const plan =
    goalCents !== null && potCents !== null && backCents !== null
      ? producerPlan(goalCents, defaults.cutBps, potCents, backCents)
      : null;
  const cut = formatBps(defaults.cutBps);

  const member = session.kind !== "visitor" ? session.username : null;
  const buzzLinks = [
    <a key="b" href="/contests" className={stepLink}>{t.browseBattles}</a>,
    null,
    <Link key="r" to="/ranking" className={stepLink}>{t.rankings}</Link>,
    member ? (
      <Link key="p" to={producerHref(member)} className={stepLink}>{t.yourPage}</Link>
    ) : (
      <Link key="j" to="/register" className={stepLink}>{t.join}</Link>
    ),
  ];

  return (
    <div data-testid="calc-producer">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">{t.assumePot}</span>
          <div className="mt-2">
            <DollarInput id="calc-pot" label={t.assumePot} value={potText ?? String(defaults.potCents / 100)} onChange={setPotText} />
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">
            {t.assumeBack} <span className="normal-case tracking-normal">({t.assumeBackSuffix})</span>
          </span>
          <div className="mt-2">
            <DollarInput id="calc-back" label={t.assumeBack} value={backText ?? String(defaults.backCents / 100)} onChange={setBackText} />
          </div>
        </label>
      </div>

      {plan && (
        <>
          <div className="mt-8 rounded-2xl border border-money/40 bg-money/10 p-5 shadow-money md:p-6">
            <p data-testid="calc-pots" className="bf-tabular font-headline text-5xl leading-none text-money md:text-6xl">
              {formatMoney(plan.potsToWinCents)}
            </p>
            <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-money">{t.potsToWin}</p>
            <p className="mt-3 text-xs leading-relaxed text-chrome-dim">{t.cutNote(cut, defaults.cutFromLive)}</p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <Stat label={t.wins} value={String(plan.wins)} testId="calc-wins" />
            <Stat label={t.fansPerPot} value={String(plan.fansPerPot)} testId="calc-fans" />
            <Stat label={t.cut} value={cut} testId="calc-cut" />
          </div>
          {focus === "money" ? (
            <Steps
              steps={t.moneySteps(plan.wins, plan.fansPerPot, formatMoney(backCents!), cut)}
              links={[<a key="b" href="/contests" className={stepLink}>{t.browseBattles}</a>]}
            />
          ) : (
            <Steps steps={t.buzzSteps(genre)} links={buzzLinks} />
          )}
          <p data-testid="calc-no-fees" className="mt-4 text-xs leading-relaxed text-chrome-dim">
            {t.noFees}
          </p>
        </>
      )}
      <SuggestedBattles contests={open} />
    </div>
  );
}

function LegRow({ leg, onBack }: { leg: FanLeg; onBack: (pick: Pick) => void }) {
  const pick = { contest: leg.contest, outcome: leg.outcome };
  return (
    <li data-testid="calc-leg" className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate font-bold text-chrome">
          @{leg.outcome.username}{" "}
          <span className="bf-tabular ml-1 text-gold" title={t.odds}>
            {leg.outcome.american_odds}
          </span>
        </p>
        <p className="truncate text-[11px] uppercase tracking-[0.12em] text-chrome-dim">
          {leg.contest.name}
          {leg.contest.is_seeded && <span className="ml-2 text-gold">{t.seeded}</span>}
        </p>
        <p className="bf-tabular mt-1 text-sm text-chrome">
          {t.stake} <span data-testid="calc-leg-stake">{formatMoney(leg.stakeCents)}</span> →{" "}
          <span data-testid="calc-leg-payout" className="font-bold text-money">
            {formatMoney(leg.payoutCents)}
          </span>{" "}
          <span className="text-chrome-dim">{t.ifWins}</span>
        </p>
      </div>
      <button
        type="button"
        onClick={() => onBack(pick)}
        aria-label={`${t.back} ${leg.outcome.username}`}
        className="bf-focusable h-10 shrink-0 rounded-md bg-money px-4 text-[11px] font-bold uppercase tracking-[0.14em] text-ground hover:bg-money-deep"
      >
        {t.back}
      </button>
    </li>
  );
}

function WaitingBattles({ contests }: { contests: LandingLiveContest[] }) {
  if (contests.length === 0) return null;
  const top = [...contests].sort((a, b) => (b.seed_cents ?? 0) - (a.seed_cents ?? 0)).slice(0, 3);
  return (
    <div className="mt-8 first:mt-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">{t.firstHeading}</p>
      <p className="mt-1 text-xs text-chrome-dim">{t.firstNote}</p>
      <ul data-testid="calc-first" className="mt-3 divide-y divide-ground-edge rounded-xl border border-ground-edge bg-ground">
        {top.map((c) => (
          <li key={c.slug} data-testid="calc-first-battle" className="flex items-center gap-3 px-4 py-3">
            <Thumb contest={c} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-chrome">{c.name}</p>
              <p className="mt-0.5 text-[11px] uppercase tracking-[0.12em] text-chrome-dim">
                {t.producers(c.outcomes.length)}
                {(c.seed_cents ?? 0) > 0 && <span className="ml-2 text-gold">{t.bonus(formatMoney(c.seed_cents!))}</span>}
              </p>
            </div>
            <a
              href={contestHref(c.slug)}
              className="bf-focusable shrink-0 rounded-md border border-money/50 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-money hover:bg-money/10"
            >
              {t.backFirst}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FanResult({
  goalCents,
  open,
  onBack,
}: {
  goalCents: number | null;
  open: LandingLiveContest[];
  onBack: (pick: Pick) => void;
}) {
  if (goalCents === null) return null;
  const markets = open.filter((c) => c.outcomes.length > 0);
  // Nobody has staked yet: no pick can be priced until a second producer is
  // backed, so these are listed apart rather than given a made-up payout.
  const priced = markets.filter((c) => stakedCents(c) > 0);
  const waiting = markets.filter((c) => stakedCents(c) <= 0);
  const singles = singlePicks(priced, goalCents);
  const stack = stackPicks(priced, goalCents);
  const ceiling = priced.length > 0 && singles.length === 0 && !stack ? bestPick(priced) : null;
  return (
    <div data-testid="calc-fan">
      {markets.length === 0 && (
        <p data-testid="calc-no-picks" className="text-sm text-chrome-dim">
          {t.noPicks}{" "}
          <a href="/contests" className="font-bold text-gold hover:text-gold-light">
            {t.browseBattles}
          </a>
        </p>
      )}
      {priced.length > 0 && singles.length === 0 && !stack && (
        <div data-testid="calc-no-reach" className="mb-8">
          {ceiling && (
            <div className="rounded-2xl border border-gold/40 bg-ground-raised p-5 md:p-6">
              <p data-testid="calc-ceiling" className="bf-tabular font-headline text-5xl leading-none text-gold">
                {formatMoney(ceiling.payoutCents - ceiling.stakeCents)}
              </p>
              <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gold">{t.mostOnePick}</p>
              <p className="mt-3 text-sm text-chrome">
                {t.mostOnePickVia(formatMoney(ceiling.stakeCents), ceiling.outcome.username, ceiling.contest.name)}
              </p>
            </div>
          )}
          <p className="mt-4 text-sm text-chrome-dim">{t.noReach}</p>
        </div>
      )}

      {singles.length > 0 && (
        <div className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">{t.singleHeading}</p>
          <ul data-testid="calc-singles" className="mt-3 divide-y divide-ground-edge rounded-xl border border-ground-edge bg-ground">
            {singles.map((leg) => (
              <LegRow key={pickKey(leg)} leg={leg} onBack={onBack} />
            ))}
          </ul>
        </div>
      )}
      {stack && (
        <div className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">{t.stackHeading(stack.length)}</p>
          <p className="mt-1 text-xs text-chrome-dim">{t.stackNote}</p>
          <ul data-testid="calc-stack" className="mt-3 divide-y divide-ground-edge rounded-xl border border-ground-edge bg-ground">
            {stack.map((leg) => (
              <LegRow key={pickKey(leg)} leg={leg} onBack={onBack} />
            ))}
          </ul>
        </div>
      )}
      <WaitingBattles contests={waiting} />
      {markets.length > 0 && <p className="mt-6 text-[11px] leading-relaxed text-chrome-dim">{t.fanFine}</p>}
    </div>
  );
}

type SaveState = { status: "idle" } | { status: "saving" } | { status: "saved"; member: boolean; key: string } | { status: "error"; error: string };

// Saves what the visitor is planning for, so the Control Room can see demand.
// A saved goal stays marked saved until an input changes.
function SaveGoal({ input }: { input: GoalInput | null }) {
  const [state, setState] = useState<SaveState>({ status: "idle" });
  const key = JSON.stringify(input);
  const saved = state.status === "saved" && state.key === key;

  const save = async () => {
    if (!input || state.status === "saving") return;
    setState({ status: "saving" });
    const res = await saveGoal(input);
    setState(res.ok ? { status: "saved", member: res.data.member, key } : { status: "error", error: res.error });
  };

  return (
    <div className="border-t border-ground-edge pt-5">
      {saved ? (
        <p data-testid="calc-saved" role="status" className="text-sm font-bold text-money">
          {state.member ? (
            t.savedMember
          ) : (
            <>
              {t.savedVisitor}{" "}
              <Link to="/register" className="text-gold hover:text-gold-light">
                {t.joinToKeep}
              </Link>
            </>
          )}
        </p>
      ) : (
        <>
          <button
            type="button"
            onClick={save}
            disabled={!input || state.status === "saving"}
            className="bf-focusable h-11 w-full rounded-md bg-money text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep disabled:cursor-not-allowed disabled:bg-ground-edge disabled:text-chrome-dim"
          >
            {state.status === "saving" ? t.saving : t.save}
          </button>
          <p className="mt-2 text-[11px] leading-relaxed text-chrome-dim">{t.saveNote}</p>
          {state.status === "error" && (
            <p role="alert" className="mt-2 text-xs font-bold text-alert">
              {state.error}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// A homepage tool that works a monthly goal backwards into battles, fans, and
// picks, from the battles open right now. It reads the landing feed the page
// already holds and never places anything: Back opens the same slip as a
// pick card.
// On its own page (page) the heading is the page's h1 and carries the
// search phrase; on the landing it is a section h2 linking to that page.
export default function EarningsCalculator({
  contests,
  onBack,
  page = false,
}: {
  contests: LandingLiveContest[];
  onBack: (pick: Pick) => void;
  page?: boolean;
}) {
  const [role, setRole] = useState<Role>("producer");
  const [goalText, setGoalText] = useState("300");
  const [genre, setGenre] = useState("");
  const [focus, setFocus] = useState<Focus>("money");
  const genres = useMemo(() => genresOf(contests), [contests]);
  const open = useMemo(() => openInGenre(contests, genre), [contests, genre]);
  const goalCents = dollarsToCents(goalText);
  const input: GoalInput | null =
    goalCents === null ? null : { role, goal_cents: goalCents, genre, ...(role === "producer" ? { focus } : {}) };

  const Heading = page ? "h1" : "h2";

  return (
    <section
      id="calculator"
      data-testid="earnings-calculator"
      className={`px-4 md:px-6 ${page ? "pb-16 pt-24 md:pb-20 md:pt-32" : "border-t border-ground-edge py-20 md:py-28"}`}
    >
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{page ? t.heading : t.eyebrow}</p>
          <Heading className="mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.01em] text-chrome md:text-6xl">
            {page ? t.pageHeading : t.heading}
          </Heading>
          <p className="mt-6 text-sm leading-relaxed text-chrome-dim md:text-base">{t.body}</p>
          {!page && (
            <a
              href={CALCULATOR_PATH}
              className="bf-focusable mt-4 inline-block text-xs font-bold uppercase tracking-[0.16em] text-gold transition-colors hover:text-gold-light"
            >
              {t.fullPage} →
            </a>
          )}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] lg:gap-10">
          <div className="space-y-6 rounded-2xl border border-ground-edge bg-ground-raised p-5 md:p-6">
            <Toggle
              label={t.role}
              value={role}
              onChange={setRole}
              options={[
                { value: "producer", label: t.producer },
                { value: "fan", label: t.fan },
              ]}
            />
            <div>
              <label htmlFor="calc-goal" className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">
                {t.goal}
              </label>
              <div className="mt-2">
                <DollarInput id="calc-goal" label={t.goalSr} value={goalText} onChange={setGoalText} big />
              </div>
              {goalCents === null && (
                <p role="alert" className="mt-2 text-xs text-alert">
                  {t.goalInvalid}
                </p>
              )}
              <div className="mt-2 flex gap-2">
                {GOAL_PRESETS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setGoalText(String(d))}
                    className="bf-focusable bf-tabular rounded-full border border-ground-edge px-3 py-1 text-xs font-bold text-chrome-dim hover:border-gold/50 hover:text-gold"
                  >
                    {formatMoney(d * 100)}
                  </button>
                ))}
              </div>
            </div>
            {genres.length > 0 && (
              <label className="block">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-chrome-dim">{t.genre}</span>
                <select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="bf-focusable mt-2 h-10 w-full rounded-lg border border-ground-edge bg-ground px-3 text-sm text-chrome"
                >
                  <option value="">{t.anyGenre}</option>
                  {genres.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {role === "producer" && (
              <Toggle
                label={t.focus}
                value={focus}
                onChange={setFocus}
                options={[
                  { value: "money", label: t.money },
                  { value: "buzz", label: t.buzz },
                ]}
              />
            )}
            <SaveGoal input={input} />
          </div>

          <div className="min-w-0">
            {role === "producer" ? (
              <ProducerResult goalCents={goalCents} genre={genre} focus={focus} open={open} />
            ) : (
              <FanResult goalCents={goalCents} open={open} onBack={onBack} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
